import { useMemo } from 'react';
import { cn } from '@/lib/utils';

export interface Candle {
  o: number;
  h: number;
  l: number;
  c: number;
  v?: number;
}

interface Props {
  candles: Candle[];
  width?: number;
  height?: number;
  /** 持仓标记线: { price, label, side } */
  markers?: Array<{ price: number; label: string; side: 'BUY' | 'SELL' }>;
  className?: string;
}

/**
 * 自实现 K 线 — 80 根蜡烛 + 网格 + 持仓标记线 + MA20 / MA50 折线。
 *
 * 生产化:替换为 lightweight-charts(README 已建议)。本组件仅作脚手架占位,
 * 数据通过 props 注入,不直连任何 API。
 */
export function CandleChart({ candles, width = 760, height = 320, markers, className }: Props) {
  const { stats, ma20, ma50 } = useMemo(() => {
    if (!candles.length) return { stats: null, ma20: [], ma50: [] };
    const min = Math.min(...candles.map((c) => c.l));
    const max = Math.max(...candles.map((c) => c.h));
    const range = max - min || 1;
    const padding = range * 0.05;
    const yMin = min - padding;
    const yMax = max + padding;
    const yRange = yMax - yMin;

    const ma = (period: number) =>
      candles.map((_, i) => {
        if (i < period - 1) return null;
        const slice = candles.slice(i - period + 1, i + 1);
        const sum = slice.reduce((acc, k) => acc + k.c, 0);
        return sum / period;
      });

    return {
      stats: { min, max, yMin, yMax, yRange },
      ma20: ma(20),
      ma50: ma(50),
    };
  }, [candles]);

  if (!stats || !candles.length) {
    return <div className={cn('flex items-center justify-center text-ink-3 text-sm', className)} style={{ width, height }}>无数据</div>;
  }

  const padX = 36; // 留给 y 轴
  const padY = 12;
  const innerW = width - padX - 8;
  const innerH = height - padY - 24;
  const stepX = innerW / candles.length;
  const candleW = Math.max(2, stepX * 0.7);

  const xToPx = (i: number) => padX + i * stepX + stepX / 2;
  const yToPx = (price: number) => padY + ((stats.yMax - price) / stats.yRange) * innerH;

  // y 轴 4 条参考线
  const ySteps = 4;
  const yLines = Array.from({ length: ySteps + 1 }, (_, i) => {
    const price = stats.yMin + (stats.yRange * i) / ySteps;
    return { price, y: yToPx(price) };
  });

  const lastClose = candles[candles.length - 1]!.c;

  const polyline = (data: (number | null)[], color: string, dash?: string) => {
    const pts = data
      .map((p, i) => (p == null ? null : `${xToPx(i)},${yToPx(p).toFixed(1)}`))
      .filter(Boolean)
      .join(' ');
    return <polyline points={pts} fill="none" stroke={color} strokeWidth={1.2} strokeDasharray={dash} />;
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} className={cn('block', className)}>
      {/* 网格 + y 轴标签 */}
      {yLines.map((line, i) => (
        <g key={i}>
          <line x1={padX} x2={width - 4} y1={line.y} y2={line.y} stroke="rgb(var(--c-line) / 0.6)" strokeDasharray="2 4" />
          <text x={4} y={line.y + 3} fontSize="10" fill="rgb(var(--c-ink-3))" className="num">
            {line.price.toFixed(2)}
          </text>
        </g>
      ))}

      {/* 蜡烛 */}
      {candles.map((c, i) => {
        const x = xToPx(i);
        const up = c.c >= c.o;
        const color = up ? 'rgb(var(--c-up))' : 'rgb(var(--c-down))';
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={yToPx(c.h)} y2={yToPx(c.l)} stroke={color} strokeWidth={1} />
            <rect
              x={x - candleW / 2}
              y={yToPx(Math.max(c.o, c.c))}
              width={candleW}
              height={Math.max(1, Math.abs(yToPx(c.o) - yToPx(c.c)))}
              fill={color}
              opacity={up ? 0.85 : 1}
            />
          </g>
        );
      })}

      {/* MA */}
      {polyline(ma20, 'rgb(var(--c-warn))')}
      {polyline(ma50, 'rgb(var(--c-purple))')}

      {/* 持仓标记线 */}
      {markers?.map((m, i) => {
        const y = yToPx(m.price);
        const tone = m.side === 'BUY' ? 'rgb(var(--c-up))' : 'rgb(var(--c-down))';
        return (
          <g key={i}>
            <line x1={padX} x2={width - 4} y1={y} y2={y} stroke={tone} strokeWidth={1} strokeDasharray="3 3" opacity={0.7} />
            <rect x={width - 88} y={y - 8} width={84} height={16} rx={3} fill={tone} />
            <text x={width - 46} y={y + 3} fontSize="10" textAnchor="middle" fill="white" className="num">
              {m.label}
            </text>
          </g>
        );
      })}

      {/* 当前价标签 */}
      <g>
        <line
          x1={padX}
          x2={width - 4}
          y1={yToPx(lastClose)}
          y2={yToPx(lastClose)}
          stroke="rgb(var(--c-brand))"
          strokeWidth={1}
        />
        <rect x={width - 60} y={yToPx(lastClose) - 8} width={56} height={16} rx={3} fill="rgb(var(--c-brand))" />
        <text x={width - 32} y={yToPx(lastClose) + 3} fontSize="10" textAnchor="middle" fill="white" className="num">
          {lastClose.toFixed(2)}
        </text>
      </g>

      {/* 图例 */}
      <g transform={`translate(${padX + 8}, ${padY + 14})`}>
        <text fontSize="10" fill="rgb(var(--c-ink-2))">
          <tspan fill="rgb(var(--c-warn))">━ MA20</tspan>
          <tspan dx="10" fill="rgb(var(--c-purple))">━ MA50</tspan>
          <tspan dx="10" fill="rgb(var(--c-brand))">━ Last</tspan>
        </text>
      </g>
    </svg>
  );
}
